/**
 * 视频理赔会话全局通道（模块级单例 + 发布订阅）
 *
 * 背景：通话界面 VideoSurveyRoom 原先挂在「工单详情」子标签内部。而工单详情页的
 * 子标签是自定义条件渲染（showformOrVideoOrPhoto === 'formReport'），坐席一切到
 * 理算信息 / 领款人 / 照片取证 / 风险预警 / 处理记录，整棵 formReport 子树被卸载，
 * VideoSurveyRoom.componentWillUnmount 随之触发 cleanupTrtc()（exitRoom + destroy），
 * 通话直接中断。
 *
 * 解决：把会话状态提升到脱离 React 树的模块级单例，房间改由 App 层的
 * VideoClaimRoomHost 常驻渲染 —— 无论怎么切子标签，通话容器都不再被卸载。
 */

// 终态：到达这些状态即视为会话结束，不再维持房间
const END_STATUSES = [
    'ENDED',
    'CANCELLED',
    'CANCELLED_BY_AGENT',
    'CANCELLED_STATE_MISMATCH',
    'CANCELLED_DEFLOSS_MISMATCH',
    'CALL_TIMEOUT',
    'CALL_CANCELLED_CUSTOMER',
    'CALL_REJECTED',
    'CALL_FAILED',
    'REJECTED',
];

let currentSession = null;
const listeners = new Set();

const notify = () => {
    listeners.forEach((fn) => {
        try {
            fn(currentSession);
        } catch (e) {
            // 单个订阅方异常不应影响其它订阅方
        }
    });
};

/** 是否终态会话（没有 session 或缺少 status 也按已结束处理） */
export const isVideoClaimEnded = (session) => {
    const status = session && session.status;
    return !status || END_STATUSES.indexOf(status) >= 0;
};

/** 当前会话（可能是 null） */
export const getVideoClaimSession = () => currentSession;

/** 订阅会话变化，返回取消订阅函数 */
export const subscribeVideoClaimSession = (listener) => {
    if (typeof listener !== 'function') return () => {};
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
};

/** 显式清空会话（通话结束 / 邀请已取消） */
export const clearVideoClaimSession = () => {
    if (currentSession === null) return null;
    currentSession = null;
    notify();
    return null;
};

/**
 * 写入会话。
 * - 终态会话统一收敛为 null；
 * - 空值会被忽略（要清空请显式调用 clearVideoClaimSession），避免切换工单、
 *   或查询某条工单返回空时，把另一个正在进行中的通话误挂断；
 * - sessionId + status 都没变时跳过通知，避免无谓重渲染。
 * @returns {object|null} 收敛后的当前会话
 */
export const publishVideoClaimSession = (session) => {
    const next = session || null;
    if (!next) return currentSession;
    if (isVideoClaimEnded(next)) return clearVideoClaimSession();
    if (currentSession
        && currentSession.sessionId === next.sessionId
        && currentSession.status === next.status) {
        return currentSession;
    }
    currentSession = next;
    notify();
    return currentSession;
};
